/**
 * Phase K Pass 2 — Eight new dashboard tile components.
 * Each card is intentionally small and self-contained: it fetches its own
 * data and renders inside the existing card grid. The outer
 * <DashboardCardShell> wrapper (in AdminDashboardPage) handles Customize
 * mode hide/show — these components don't need to know about that.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    FileSignature, ShieldAlert, Truck, AlertTriangle, Repeat,
    Server, Mail, History, ArrowUpRight, CheckCircle2, XCircle, Clock,
} from "lucide-react";

function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const min = Math.floor(diff / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min}m ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const d = Math.floor(hr / 24);
        return `${d}d ago`;
    } catch {
        return iso;
    }
}

// =====================================================
// Pending signatures — Contracts & Compliance
// =====================================================
export function PendingSignaturesCard({ stats }) {
    const count = stats?.contracts_pending_signature ?? 0;
    const oldest = stats?.oldest_pending_days;
    const accent = count > 0 ? "bg-amber-50 text-amber-900" : "bg-emerald-50 text-emerald-900";
    return (
        <Link
            to="/admin/contracts?status=sent_for_signature"
            data-testid="admin-card-link-pending-signatures"
            className="block h-full"
        >
            <Card data-testid="admin-card-pending-signatures" className="h-full hover:border-slate-300 transition-colors">
                <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base font-semibold text-slate-900">Pending signatures</CardTitle>
                        <CardDescription className="text-sm text-slate-500">
                            {count > 0 && oldest != null
                                ? `Oldest sent ${oldest} day${oldest === 1 ? "" : "s"} ago.`
                                : "Contracts customers haven't signed yet."}
                        </CardDescription>
                    </div>
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
                        <FileSignature size={18} />
                    </span>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end justify-between">
                        <span className="font-display text-4xl font-bold tracking-tight text-slate-900" data-testid="admin-card-value-pending-signatures">
                            {count}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-900">
                            Open contracts <ArrowUpRight size={14} />
                        </span>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

// =====================================================
// COIs expiring soon — Contracts & Compliance
// =====================================================
export function CoiExpiringCard({ stats }) {
    const count = stats?.coi_expiring_le_30 ?? 0;
    const accent =
        count >= 5 ? "bg-red-50 text-red-700" :
        count > 0 ? "bg-amber-50 text-amber-900" :
        "bg-emerald-50 text-emerald-900";
    return (
        <Link
            to="/admin/rentals?coi_expiring=1"
            data-testid="admin-card-link-coi-expiring"
            className="block h-full"
        >
            <Card data-testid="admin-card-coi-expiring" className="h-full hover:border-slate-300 transition-colors">
                <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base font-semibold text-slate-900">COIs expiring soon</CardTitle>
                        <CardDescription className="text-sm text-slate-500">
                            Insurance certificates expiring in 30 days or less.
                        </CardDescription>
                    </div>
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
                        <ShieldAlert size={18} />
                    </span>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end justify-between">
                        <span className="font-display text-4xl font-bold tracking-tight text-slate-900" data-testid="admin-card-value-coi-expiring">
                            {count}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-900">
                            View rentals <ArrowUpRight size={14} />
                        </span>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

// =====================================================
// Active rentals KPI — Rentals & Equipment
// =====================================================
export function ActiveRentalsCard({ stats }) {
    const active = stats?.rentals_active ?? 0;
    const scheduled = stats?.rentals_scheduled ?? 0;
    const ended = stats?.rentals_ended_30d ?? 0;
    return (
        <Link
            to="/admin/rentals?status=active"
            data-testid="admin-card-link-active-rentals"
            className="block h-full"
        >
            <Card data-testid="admin-card-active-rentals" className="h-full hover:border-slate-300 transition-colors">
                <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base font-semibold text-slate-900">Active rentals</CardTitle>
                        <CardDescription className="text-sm text-slate-500">
                            Live · upcoming · recently ended.
                        </CardDescription>
                    </div>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-900">
                        <Truck size={18} />
                    </span>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-2.5">
                            <div className="text-emerald-700">Active</div>
                            <div className="mt-1 font-display text-lg font-bold text-emerald-700 tabular-nums" data-testid="admin-card-value-active-rentals">
                                {active}
                            </div>
                        </div>
                        <div className="rounded-md border border-slate-200 p-2.5">
                            <div className="text-slate-500">Scheduled</div>
                            <div className="mt-1 font-display text-lg font-bold text-slate-900 tabular-nums" data-testid="admin-card-value-active-rentals-scheduled">
                                {scheduled}
                            </div>
                        </div>
                        <div className="rounded-md border border-slate-200 p-2.5">
                            <div className="text-slate-500">Ended (30d)</div>
                            <div className="mt-1 font-display text-lg font-bold text-slate-900 tabular-nums" data-testid="admin-card-value-active-rentals-ended">
                                {ended}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

// =====================================================
// Schedules at risk — Billing & Invoicing
// =====================================================
export function SchedulesAtRiskCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/billing-schedules/at-risk-count");
                setData(data);
            } catch (err) {
                console.warn("schedules-at-risk failed", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);
    const count = data?.count ?? 0;
    const accent = count > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-900";
    return (
        <Link
            to="/admin/billing-schedules?filter=at_risk"
            data-testid="admin-card-link-schedules-at-risk"
            className="block h-full"
        >
            <Card data-testid="admin-card-schedules-at-risk" className="h-full hover:border-slate-300 transition-colors">
                <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base font-semibold text-slate-900">Schedules at risk</CardTitle>
                        <CardDescription className="text-sm text-slate-500">
                            Auto-charge schedules with no saved payment method.
                        </CardDescription>
                    </div>
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
                        <AlertTriangle size={18} />
                    </span>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end justify-between">
                        <span className="font-display text-4xl font-bold tracking-tight text-slate-900" data-testid="admin-card-value-schedules-at-risk">
                            {loading ? "…" : count}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-900">
                            Review <ArrowUpRight size={14} />
                        </span>
                    </div>
                    {(data?.top_5_schedules || []).length > 0 && (
                        <ul className="mt-3 space-y-1 text-xs text-slate-600">
                            {data.top_5_schedules.slice(0, 3).map((s) => (
                                <li key={s.schedule_id} className="truncate">
                                    · {s.company_name || "—"}{s.unit_number ? ` · #${s.unit_number}` : ""}
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </Link>
    );
}

// =====================================================
// Auto-charge attempts summary — Payments & Reconciliation
// =====================================================
export function AutoChargeAttemptsSummaryCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/auto-charge-attempts/summary");
                setData(data);
            } catch (err) {
                console.warn("auto-charge-summary failed", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);
    const failed = data?.failed_month ?? 0;
    const abandoned = data?.abandoned_month ?? 0;
    const scheduled = data?.scheduled_pending ?? 0;
    return (
        <Card data-testid="admin-card-auto-charge-attempts-summary" className="h-full">
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                <div className="space-y-1.5">
                    <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                        <Repeat size={16} className="text-blue-900" />
                        Auto-charge attempts
                    </CardTitle>
                    <CardDescription className="text-sm text-slate-500">
                        {data?.month ? `Failures + scheduled retries (month ${data.month}).` : "Failures + scheduled retries."}
                    </CardDescription>
                </div>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-2 text-xs">
                <Link
                    to="/admin/auto-charge-attempts?status=failed"
                    data-testid="admin-card-link-auto-charge-failed"
                    className="rounded-md border border-red-200 bg-red-50/40 p-2.5 transition-colors hover:bg-red-50"
                >
                    <div className="text-red-700">Failed</div>
                    <div className="mt-1 font-display text-lg font-bold text-red-700 tabular-nums">
                        {loading ? "—" : failed}
                    </div>
                </Link>
                <Link
                    to="/admin/auto-charge-attempts?status=abandoned"
                    data-testid="admin-card-link-auto-charge-abandoned"
                    className="rounded-md border border-amber-200 bg-amber-50/40 p-2.5 transition-colors hover:bg-amber-50"
                >
                    <div className="text-amber-800">Abandoned</div>
                    <div className="mt-1 font-display text-lg font-bold text-amber-800 tabular-nums">
                        {loading ? "—" : abandoned}
                    </div>
                </Link>
                <Link
                    to="/admin/auto-charge-attempts?status=pending,processing"
                    data-testid="admin-card-link-auto-charge-scheduled"
                    className="rounded-md border border-slate-200 p-2.5 transition-colors hover:bg-slate-50"
                >
                    <div className="text-slate-600">Scheduled</div>
                    <div className="mt-1 font-display text-lg font-bold text-slate-900 tabular-nums">
                        {loading ? "—" : scheduled}
                    </div>
                </Link>
            </CardContent>
        </Card>
    );
}

// =====================================================
// Cron health — System & Activity (super-admin only)
// =====================================================
export function CronHealthCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/dashboard/cron-health");
                setData(data);
            } catch (err) {
                // Silent — most likely a regular admin hit this; the
                // dashboard already gated us out, but keep the page stable.
                console.warn("cron-health failed", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);
    const rows = data?.crons || [];
    return (
        <Card data-testid="admin-card-cron-health" className="h-full col-span-1 md:col-span-2 lg:col-span-3">
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                <div className="space-y-1.5">
                    <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                        <Server size={16} className="text-blue-900" />
                        Cron health
                    </CardTitle>
                    <CardDescription className="text-sm text-slate-500">
                        Last + next run times for all scheduled background jobs.
                    </CardDescription>
                </div>
            </CardHeader>
            <CardContent>
                {loading && <p className="text-sm text-slate-500">Loading…</p>}
                {!loading && rows.length === 0 && (
                    <p className="text-sm text-slate-500">No crons registered.</p>
                )}
                {!loading && rows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                                <tr className="border-b border-slate-200">
                                    <th className="text-left py-2 pr-3">Job</th>
                                    <th className="text-left py-2 pr-3">Last run</th>
                                    <th className="text-left py-2 pr-3">Next run</th>
                                    <th className="text-left py-2 pr-3">Flag</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => (
                                    <tr key={c.name} className="border-b border-slate-100 last:border-0" data-testid={`cron-row-${c.name}`}>
                                        <td className="py-2 pr-3 font-medium text-slate-900">{c.label}</td>
                                        <td className="py-2 pr-3 text-slate-700">
                                            {c.last_run_at ? (
                                                <span className={c.stale ? "text-amber-800" : "text-slate-700"}>
                                                    {c.last_success === false && <XCircle size={12} className="inline mr-1 text-red-600" />}
                                                    {c.last_success !== false && <CheckCircle2 size={12} className="inline mr-1 text-emerald-600" />}
                                                    {fmtRel(c.last_run_at)}
                                                </span>
                                            ) : (
                                                <span className="text-slate-400">— never recorded</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3 text-slate-600">
                                            {c.next_run_at ? (
                                                <span className="inline-flex items-center gap-1"><Clock size={11} className="text-slate-400" />{fmtRel(c.next_run_at)}</span>
                                            ) : (
                                                <span className="text-slate-400">paused</span>
                                            )}
                                        </td>
                                        <td className="py-2 pr-3">
                                            {c.enabled_flag_key ? (
                                                <span className={
                                                    c.enabled
                                                        ? "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset bg-emerald-50 text-emerald-900 ring-emerald-200"
                                                        : "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset bg-slate-100 text-slate-700 ring-slate-200"
                                                }>
                                                    {c.enabled ? "Enabled" : "Disabled"}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-slate-400 uppercase tracking-wider">always on</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// =====================================================
// Email failures — System & Activity (super-admin only)
// =====================================================
export function EmailFailuresCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/email-log/24h-summary");
                setData(data);
            } catch (err) {
                console.warn("email-failures failed", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);
    const failed = data?.failed ?? 0;
    const sent = data?.sent ?? 0;
    const accent = failed > 0 ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-900";
    return (
        <Link
            to="/admin/email-log"
            data-testid="admin-card-link-email-failures"
            className="block h-full"
        >
            <Card data-testid="admin-card-email-failures" className="h-full hover:border-slate-300 transition-colors">
                <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base font-semibold text-slate-900">Email failures (24h)</CardTitle>
                        <CardDescription className="text-sm text-slate-500">
                            {loading ? "Last 24 hours of outbound email." : `${sent} sent · ${failed} failed in the last 24h.`}
                        </CardDescription>
                    </div>
                    <span className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${accent}`}>
                        <Mail size={18} />
                    </span>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end justify-between">
                        <span className="font-display text-4xl font-bold tracking-tight text-slate-900" data-testid="admin-card-value-email-failures">
                            {loading ? "…" : failed}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-900">
                            Open log <ArrowUpRight size={14} />
                        </span>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

// =====================================================
// Impersonations — System & Activity (super-admin only)
// =====================================================
export function ImpersonationsCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/impersonation-audit/7d-summary");
                setData(data);
            } catch (err) {
                console.warn("impersonations failed", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);
    const sessions = data?.sessions ?? 0;
    const admins = data?.distinct_admins ?? 0;
    return (
        <Link
            to="/admin/impersonation-audit"
            data-testid="admin-card-link-impersonations-7d"
            className="block h-full"
        >
            <Card data-testid="admin-card-impersonations-7d" className="h-full hover:border-slate-300 transition-colors">
                <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base font-semibold text-slate-900">Impersonations (7d)</CardTitle>
                        <CardDescription className="text-sm text-slate-500">
                            {loading ? "Sessions in the last 7 days." : `${admins} distinct admin${admins === 1 ? "" : "s"} · last 7 days.`}
                        </CardDescription>
                    </div>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-800">
                        <History size={18} />
                    </span>
                </CardHeader>
                <CardContent>
                    <div className="flex items-end justify-between">
                        <span className="font-display text-4xl font-bold tracking-tight text-slate-900" data-testid="admin-card-value-impersonations-7d">
                            {loading ? "…" : sessions}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-blue-900">
                            Open audit <ArrowUpRight size={14} />
                        </span>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}

export { fmtMoney as _fmtMoney, fmtRel as _fmtRel };
